import { Link, makeStyles, tokens } from '@fluentui/react-components';
import { DataUsage20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TitledCard from '../Layout/TitledCard';
import type { HomePipelineStage, PipelineTone } from './homeTypes';

const TONE_COLORS: Record<PipelineTone, string> = {
  neutral: tokens.colorNeutralForeground4,
  info: tokens.colorBrandBackground,
  warning: tokens.colorPaletteMarigoldBackground3,
  success: tokens.colorPaletteGreenBackground3,
};

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    height: '10px',
    columnGap: '2px',
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
  },
  count: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
});

const WorkPipeline = ({ stages }: { stages: HomePipelineStage[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles();
  return (
    <TitledCard
      title={t('workPipeline', 'Work pipeline')}
      icon={<DataUsage20Regular />}
      actions={
        <Link as="button" onClick={() => navigate('/works')}>
          {t('allWorks', 'All works')}
        </Link>
      }
    >
      <div
        className={styles.bar}
        role="img"
        aria-label={t('workPipeline', 'Work pipeline')}
      >
        {stages.map((stage) => (
          <span
            key={stage.status}
            style={{
              flexGrow: stage.count,
              backgroundColor: TONE_COLORS[stage.tone],
            }}
          />
        ))}
      </div>
      <ul className={styles.legend}>
        {stages.map((stage) => (
          <li key={stage.status} className={styles.legendItem}>
            <span
              className={styles.dot}
              style={{ backgroundColor: TONE_COLORS[stage.tone] }}
            />
            {stage.status}
            <span className={styles.count}>{stage.count}</span>
          </li>
        ))}
      </ul>
    </TitledCard>
  );
};

export default WorkPipeline;
